import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Pencil, Trash2 } from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import type { ApiError } from '@pms/shared';
import {
  requirementsService,
  type RequirementCategory,
  type RequirementStatus,
} from '@/services/requirements.service';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { Tabs } from '@/components/ui/tabs';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CategoryFormDialog } from './components/CategoryFormDialog';
import { StatusFormDialog } from './components/StatusFormDialog';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

const TABS = [
  { key: 'categories', label: 'Categories' },
  { key: 'statuses', label: 'Statuses' },
  { key: 'settings', label: 'Settings' },
];

// ── Categories Tab ────────────────────────────────────────────────────────────

type CatDialog = { mode: 'create' | 'edit'; category?: RequirementCategory } | null;

function CategoriesTab({ categories, loading, onRefresh }: {
  categories: RequirementCategory[];
  loading: boolean;
  onRefresh: () => void;
}) {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<CatDialog>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string; count: number } | null>(null);

  const deleteMut = useMutation({
    mutationFn: (id: string) => requirementsService.deleteCategory(id),
    onSuccess: () => {
      toast.success('Category deleted');
      void queryClient.invalidateQueries({ queryKey: ['requirements', 'categories'] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const columns: Column<RequirementCategory>[] = [
    { key: 'name', header: 'Name', cell: (r) => <span className="font-medium">{r.name}</span> },
    { key: 'code', header: 'Code', cell: (r) => <code className="font-mono text-xs">{r.code}</code> },
    {
      key: 'description',
      header: 'Description',
      cell: (r) => r.description
        ? <span className="text-sm text-muted-foreground truncate max-w-xs block">{r.description}</span>
        : <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'count',
      header: 'Requirements',
      cell: (r) => (
        <Badge variant="secondary">{r.requirements_count}</Badge>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      className: 'text-right',
      cell: (r) => {
        const canDelete = r.requirements_count === 0;
        return (
          <div className="flex items-center justify-end gap-0.5">
            <PermissionGate permission={PERMISSIONS.REQUIREMENTS_MASTER}>
              <button
                type="button"
                onClick={() => setDialog({ mode: 'edit', category: r })}
                className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Edit"
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                type="button"
                disabled={!canDelete}
                onClick={() => canDelete && setDeleteTarget({ id: r.id, name: r.name, count: r.requirements_count })}
                className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:pointer-events-none disabled:opacity-40"
                aria-label="Delete"
                title={!canDelete ? 'Cannot delete: category has requirements' : undefined}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </PermissionGate>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="flex justify-end mb-4">
        <PermissionGate permission={PERMISSIONS.REQUIREMENTS_MASTER}>
          <Button onClick={() => setDialog({ mode: 'create' })}>+ Add Category</Button>
        </PermissionGate>
      </div>
      <DataTable
        columns={columns}
        rows={categories}
        rowKey={(r) => r.id}
        loading={loading}
        empty="No categories yet."
      />
      {dialog && (
        <CategoryFormDialog
          open
          mode={dialog.mode}
          category={dialog.category}
          onClose={() => setDialog(null)}
          onSuccess={() => { setDialog(null); onRefresh(); }}
        />
      )}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              Delete &quot;{deleteTarget?.name}&quot;? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { if (deleteTarget) { deleteMut.mutate(deleteTarget.id); setDeleteTarget(null); } }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ── Statuses Tab ──────────────────────────────────────────────────────────────

type StatusDialog = { mode: 'create' | 'edit'; status?: RequirementStatus } | null;

function StatusesTab({ statuses, loading, onRefresh }: {
  statuses: RequirementStatus[];
  loading: boolean;
  onRefresh: () => void;
}) {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<StatusDialog>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const deleteMut = useMutation({
    mutationFn: (id: string) => requirementsService.deleteStatus(id),
    onSuccess: () => {
      toast.success('Status deleted');
      void queryClient.invalidateQueries({ queryKey: ['requirements', 'statuses'] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const columns: Column<RequirementStatus>[] = [
    {
      key: 'color',
      header: 'Color',
      className: 'w-20',
      cell: (r) => (
        <div className="flex items-center gap-2">
          <span className="h-4 w-4 rounded-full shrink-0 border border-border/40" style={{ backgroundColor: r.color }} />
          <code className="text-xs">{r.color}</code>
        </div>
      ),
    },
    { key: 'name', header: 'Name', cell: (r) => <span className="font-medium">{r.name}</span> },
    { key: 'code', header: 'Code', cell: (r) => <code className="font-mono text-xs">{r.code}</code> },
    { key: 'order', header: 'Order', cell: (r) => r.display_order },
    {
      key: 'default',
      header: 'Default',
      cell: (r) => r.is_default
        ? <Badge variant="success">Default</Badge>
        : <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      className: 'text-right',
      cell: (r) => {
        const canDelete = !r.is_default;
        return (
          <div className="flex items-center justify-end gap-0.5">
            <PermissionGate permission={PERMISSIONS.REQUIREMENTS_MASTER}>
              <button
                type="button"
                onClick={() => setDialog({ mode: 'edit', status: r })}
                className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Edit"
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                type="button"
                disabled={!canDelete}
                onClick={() => canDelete && setDeleteTarget({ id: r.id, name: r.name })}
                className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:pointer-events-none disabled:opacity-40"
                aria-label="Delete"
                title={r.is_default ? 'Cannot delete the default status' : undefined}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </PermissionGate>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="flex justify-end mb-4">
        <PermissionGate permission={PERMISSIONS.REQUIREMENTS_MASTER}>
          <Button onClick={() => setDialog({ mode: 'create' })}>+ Add Status</Button>
        </PermissionGate>
      </div>
      <DataTable
        columns={columns}
        rows={statuses}
        rowKey={(r) => r.id}
        loading={loading}
        empty="No statuses yet."
      />
      {dialog && (
        <StatusFormDialog
          open
          mode={dialog.mode}
          status={dialog.status}
          onClose={() => setDialog(null)}
          onSuccess={() => { setDialog(null); onRefresh(); }}
        />
      )}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Status</AlertDialogTitle>
            <AlertDialogDescription>
              Delete &quot;{deleteTarget?.name}&quot;? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { if (deleteTarget) { deleteMut.mutate(deleteTarget.id); setDeleteTarget(null); } }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ── Settings Tab ──────────────────────────────────────────────────────────────

function SettingsTab() {
  return (
    <Card className="max-w-lg">
      <CardHeader>
        <CardTitle>Requirements Settings</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">No settings configured yet.</p>
      </CardContent>
    </Card>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function RequirementsMasterPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') ?? 'categories';
  const setTab = (key: string) => setSearchParams({ tab: key });

  const categoriesQuery = useQuery({
    queryKey: ['requirements', 'categories'],
    queryFn: () => requirementsService.listCategories(),
  });

  const statusesQuery = useQuery({
    queryKey: ['requirements', 'statuses'],
    queryFn: () => requirementsService.listStatuses(),
  });

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-semibold text-foreground">Requirements Master</h1>

      <Tabs tabs={TABS} active={activeTab} onChange={setTab} />

      <div className="mt-4">
        {activeTab === 'categories' && (
          <CategoriesTab
            categories={categoriesQuery.data ?? []}
            loading={categoriesQuery.isLoading}
            onRefresh={() => void categoriesQuery.refetch()}
          />
        )}
        {activeTab === 'statuses' && (
          <StatusesTab
            statuses={statusesQuery.data ?? []}
            loading={statusesQuery.isLoading}
            onRefresh={() => void statusesQuery.refetch()}
          />
        )}
        {activeTab === 'settings' && <SettingsTab />}
      </div>
    </div>
  );
}
